import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { Box, Spinner } from '@chakra-ui/react';

import { useHeader } from '@app/components/common/navigation/hooks/header-service';
import { useAlert } from '@app/hooks/use-alert';
import { api } from '@app/api';
import { MasterOutletUpdate } from '@app/api/models/acb';
import { useGuide } from '@app/hooks/use-guide';
import { Paper } from '@app/components/common';
import Error404 from '@app/pages/errors/404';
import { usePermissions } from '@app/hooks/use-permissions';
import { ROLES } from '@app/navigation/types';
import { useAutocomplete } from '@app/hooks';

import { MasterOutletFormInstance } from '../outlets/types';
import OutletForm from '../outlets/update-form';
import { onlyProcessFields } from './utils/process-type';

export const ViewUpdateOutlet = () => {
  useHeader('Просмотр заявки на изменение мастер ТТ', []);
  const params = useParams<{ id: string }>();

  const { state } = useLocation<{ update?: MasterOutletUpdate }>();
  const history = useHistory();
  const { error } = useAlert();
  const getAutocomplete = useAutocomplete();

  const [instance, setInstance] = useState<
    MasterOutletFormInstance | undefined
  >(undefined);
  const [origin, setOrigin] = useState<MasterOutletFormInstance>();

  const getMasterOutletStatus = useGuide(
    'outlet_status',
    undefined,
  );

  const getChannels = useGuide('outlet_sub_channel', undefined);

  useEffect(() => {
    void (async () => {
      if (!params.id || !state?.update) {
        return;
      }
      const update = onlyProcessFields(state.update);
      const { data: response } = await api.acb.getMasterOutlet(Number(params.id));
      if (response.code !== 200) {
        error(response.message);
        history.goBack();
        return;
      }
      const outlet = response.data;

      let region = await getAutocomplete('region', { search: outlet.address.region });
      let city = await getAutocomplete('city', { search: outlet.address.city });

      setOrigin({
        ...outlet,
        address: {
          ...outlet.address,
          region: region?.[0],
          city: city?.[0],
        },
      });

      let channel, subChannel, status, routeNumber: EntityBase;

      if (update.data.status_id !== undefined) {
        const statuses = await getMasterOutletStatus();
        status = statuses.find(({ id }) => id === update.data.status_id);
      }

      if (update.data.sub_channel_id !== undefined) {
        const rawChannels = await getChannels();
        const channels = rawChannels.reduce<EntityBase[]>((acc, item, index) => {
          if (acc.find(({ name }) => name === item.channel)) {
            return acc;
          }
          return [...acc, { id: index, name: item.channel }];
        }, []);
        subChannel = rawChannels.find(({ id }) => id === update.data.sub_channel_id);
        channel = channels.find(({ name }) => name.toLowerCase() === subChannel?.channel?.toLowerCase());
      }

      region = await getAutocomplete('region', { search: update.data.address?.region || outlet.address.region });
      city = await getAutocomplete('city', { search: update.data.address?.city || outlet.address.city });

      setInstance({
        ...outlet,
        name: update.data?.name || outlet?.name,
        address: {
          ...update.data?.address || outlet?.address,
          region: region?.[0],
          city: city?.[0],
        },
        contact_person_1: update.data.contact_person_1 || outlet.contact_person_1,
        contact_person_2: update.data.contact_person_2 || outlet.contact_person_2,
        director: update.data.director || outlet.director,
        email: update.data.email || outlet.email,
        phone: update.data.phone || outlet.phone,
        status: status || outlet.status,
        channel: channel || outlet.channel,
        sub_channel: subChannel || outlet.sub_channel,
        subnet: update.data.subnet || outlet.subnet,
        title: update.data.title || outlet.title,
        visits_day: update.data.visits_day,
        updater_user: update.user,
      });
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, state]);

  const onFormCancel = () => {
    history.push('/acl/updates-outlets/');
  };

  const canEdit = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF] });

  if (!canEdit) {
    return <Error404 />;
  }

  return (
    <Box paddingX={{ xs: 0, sm: 2, md: 4, lg: 12, xl: 22 }}>
      {!instance ? (
        <Box
          width="100%"
          height="300px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner />
        </Box>
      ) : (
        <Paper>
          <Box paddingX={4} paddingY={6}>
            <OutletForm
              instance={instance}
              origin={origin}
              updateDistributors={onlyProcessFields(state.update).data.distributors}
              onCancel={onFormCancel}
              readOnly
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ViewUpdateOutlet;
